import * as React from 'react';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import DetailsBlock from '../components/tables/DetailsBlock';
import { DetailsValueBlock } from '../components/tables/DetailsValueBlock';
import { localizations } from '../localization/localization';
import DetailsContainer from '../components/tables/DetailsContainer';
import { UnitModel, UnitProperties, UnitType } from './UnitModels';
import { renderEmailDetails, renderPersonDetails, renderPhoneDetails, renderCompanyDetails, renderWebsiteDetails, renderAnnouncementCountDetails } from '../components/tables/DetailsHelpers';
import { EditItemProps, DeleteItemProps, CopyItemProps, MessagingProps } from '../common/props/itemProps';
import { toCommaDecimal } from '../utils/numberUtils';
import * as UnitStore from '../units/store/unitStore'
import { ApplicationState } from "../store";
import { connect } from 'react-redux';
import ZeckitSmallWidget from '../common/ZeckitSmallWidget';
import NotificationsIcon from '@material-ui/icons/Notifications';
import NotificationsOffIcon from '@material-ui/icons/NotificationsOff';
import { Tooltip } from 'react-leaflet';

interface UnitDetailsProps extends UnitModel, EditItemProps<UnitModel>, DeleteItemProps<UnitModel>, CopyItemProps<UnitModel>, MessagingProps<UnitModel> {
    onGoToMap: (unit: UnitModel) => void;
}

interface RouteProps  { 
    units: UnitStore.FreeUnitState
}

const renderProperties = (properties: UnitProperties) => {
    return <Grid container direction="column">
        {properties.adr ? <Grid item><Typography>{localizations.trucks.properties.adr}</Typography></Grid> : null}
        {properties.thermal ? <Grid item><Typography>{localizations.trucks.properties.thermal}</Typography></Grid> : null}
        {properties.glass ? <Grid item><Typography>{localizations.trucks.properties.glass}</Typography></Grid> : null}
        {properties.tank ? <Grid item><Typography>{localizations.trucks.properties.tank}</Typography></Grid> : null}
        {properties.mega ? <Grid item><Typography>{localizations.trucks.properties.mega}</Typography></Grid> : null}
        {properties.explosive ? <Grid item><Typography>{localizations.trucks.properties.explosive}</Typography></Grid> : null}
        {properties.pieceGoods ? <Grid item><Typography>{localizations.trucks.properties.pieceGoods}</Typography></Grid> : null}
        {properties.bulkGoods ? <Grid item><Typography>{localizations.trucks.properties.bulkGoods}</Typography></Grid> : null}
    </Grid>
}

const renderRoutes = (props: any) => {
    const units = props.units.routes
    let length = 0
    Object.keys(units).forEach((value, index) => {
        if (props.destinationCity !== null && units[value] !== undefined && units[value].to.id === props.destinationCity.id && units[value].from.id === props.departureCity.id && units[value].to.id === props.destinationCity.id && units[value].distance !== null)  {
            
            length = Math.round(units[value].distance)
        }
        
    });
    if (length !== 0) {
        return <span>~ {length.toFixed()} km</span>
    }
    else {
        return <span/>
    } 
}

const renderPrice = (props: any) => {

    const units = props.units.routes
    let price = 0.00
    let length = 0
    Object.keys(units).forEach((value, index) => {
        if (props.destinationCity !== null && units[value] !== undefined && units[value].from.id === props.departureCity.id &&  units[value].to.id === props.destinationCity.id && units[value].distance !== null) {
          length = Math.round(units[value].distance)
          price = props.contractPrice / length
        }
    });
    if (price !== 0.00) {
        return <span>~ {price.toFixed(2)} €/km</span>
    }
    else {
        return null
    }
}

function pad(num: number) {
  return ("0"+num).slice(-2);
}
function hhmmss(secs: number) {
  let minutes = Math.floor(secs / 60);
  secs = secs%60;
  const hours = Math.floor(minutes/60);
  minutes = minutes%60;
  return `${pad(hours)}:${pad(minutes)}:${pad(secs)}`;
}
function hmin(secs: number) {
  let minutes = Math.floor(secs / 60);
  secs = secs%60;
  const hours = Math.floor(minutes/60);
  minutes = minutes%60;
    
  if (hours < 1) {
    return `${minutes}${localizations.common.min}`;
  }
  return `${hours}${localizations.common.h} ${pad(minutes)}${localizations.common.min}`;
}

const renderTimeEstimate = (props: any) => {

    const units = props.units.routes
    let length = 0
    let secs = 0
    let formattedTime = ''
    Object.keys(units).forEach((value, index) => {
      if (props.destinationCity !== null && units[value] !== undefined && units[value].from.id === props.departureCity.id &&  units[value].to.id === props.destinationCity.id && units[value].distance !== null) {
          length = Math.round(units[value].distance)
          secs = Math.ceil(length / 80 * 3600)
          formattedTime = hmin(secs)
        }
    });
    if (formattedTime !== '') {
        return <span>~ {formattedTime} {localizations.common.speedUsedForCalc}</span>
    }
    else {
        return null
    }
  }

type UnitRouteProps = UnitDetailsProps & RouteProps

const FreeUnitDetails: React.SFC<UnitRouteProps> = (props) => {

    const trackingStr = (props.isDistanceCheckEnabled ? 
        `${localizations.common.trackingActivated} ${props.searchDistance} ${localizations.common.km}` : 
        `${localizations.common.trackingNotActivated}`)

    const NotificationIcons = () => (
        (props.isOwn ? <Grid item title={trackingStr}>
            {props.isDistanceCheckEnabled ? <NotificationsIcon color="primary" /> : <NotificationsOffIcon color="error" />}
        </Grid> : null)
    );

    const Buttons = () => (
        <Grid container spacing={2}>
            {props.isEditEnabled && props.isEditEnabled(props) ? <Grid item><Button variant="outlined" color="primary" onClick={() => props.onEdit && props.onEdit(props)}>{localizations.actions.edit}</Button></Grid> : null}
            {props.isDeleteEnabled && props.isDeleteEnabled(props) ? <Grid item><Button variant="outlined" color="primary" onClick={() => props.onDelete && props.onDelete(props)}>{localizations.actions.delete}</Button></Grid> : null}
            {props.isCopyEnabled && props.isCopyEnabled(props) ? <Grid item><Button variant="outlined" color="primary" onClick={() => props.onCopy(props)}>{localizations.units.actions.copy}</Button></Grid> : null}
            <Grid item>
                <Button variant="outlined" color="primary" onClick={() => props.onSendEmail(props)}>{localizations.actions.sendInfoByEmail}</Button>
            </Grid>
            <Grid item>
                <Button variant="outlined" color="primary" onClick={() => props.onSendEmailToReporter(props)}>{localizations.actions.sendEmailToReporter}</Button>
            </Grid>
            <Grid item>
                <Button variant="outlined" color="primary" onClick={(e) => props.onGoToMap(props)}>{localizations.actions.showOnMap}</Button>
            </Grid>
        </Grid>
    );

    return (
        <DetailsContainer>
            <DetailsBlock
                title={localizations.units.unitDetailsHeader}
                itemsRightOfTitle={<NotificationIcons />}
                rightItems={<Buttons />}>
                {renderPersonDetails(props.coordinator, localizations.common.reporter)}
                <DetailsValueBlock title={localizations.common.properties}>
                    {renderProperties(props.properties)}
                </DetailsValueBlock>
                {props.trailerIdentifier && props.trailerIdentifier.length > 0 && props.trailerIdentifier !== UnitType.Unspecified ?
                    <DetailsValueBlock title={props.trailerType === UnitType.Trailer ? localizations.trucks.licensePlateNumber : localizations.units.containerNumber}>
                        <Typography>{props.trailerIdentifier}</Typography>
                    </DetailsValueBlock>
                    : null}
                {props.contractPrice ? <DetailsValueBlock title={localizations.common.contractPriceCol}>
                    <Typography>{`${toCommaDecimal(props.contractPrice)} €`}</Typography>
                </DetailsValueBlock> : null}
                <DetailsValueBlock title={localizations.trucks.distance}>
                    <Typography>{renderRoutes(props)}</Typography>
                </DetailsValueBlock>
                <DetailsValueBlock title={localizations.trucks.time}>
                    <Typography>{renderTimeEstimate(props)}</Typography>
                </DetailsValueBlock>
                <DetailsValueBlock title={localizations.common.totalPrice}>
                    <Typography>{renderPrice(props)}</Typography>
                </DetailsValueBlock>
                {props.details ? <DetailsValueBlock title={localizations.common.details}>
                    <Typography>{props.details}</Typography>
                </DetailsValueBlock> : null}
            </DetailsBlock>
            {props.company ? <DetailsBlock 
                title={localizations.units.companyDetailsHeader}
                rightItems={<ZeckitSmallWidget businessId={props.company.businessId} maxWidth={50}/>}
            >
                {renderCompanyDetails(props.company)}
                {renderWebsiteDetails(props.company.website)}
                {props.company.phoneContacts.map((item, index) => <React.Fragment key={index}>
                    {renderPhoneDetails(item, index + 1)}
                </React.Fragment>)}
                {props.company.emailContacts.map((item, index) => <React.Fragment key={index}>
                    {renderEmailDetails(item, index + 1)}
                </React.Fragment>)}
                <React.Fragment>
                    {renderAnnouncementCountDetails(props.company.announcementCount)}
                </React.Fragment>
            </DetailsBlock> : null}
        </DetailsContainer>
    );
}

const mapStateToProps = (state: ApplicationState) => {
    const { units } = state;
    return { units };
}

export default connect<any, any, any>(mapStateToProps)(FreeUnitDetails);


// WEBPACK FOOTER //
// ./src/units/FreeUnitDetails.tsx